import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';

import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import Icon from '@mui/material/Icon';

const ListItemComponent = React.forwardRef(
  ({ className, to, ...props }, ref) => {
    const location = useLocation();

    const getClassName = useMemo(
      () => (navData) => {
        if (to.pathname === '/dashboard') {
          return `${className} ${
            location.pathname === '/dashboard' ||
            location.pathname.startsWith('/benefits') ||
            location.pathname.startsWith('/perks')
              ? 'active'
              : ''
          }`;
        }
        return `${className} ${navData.isActive ? 'active' : ''}`;
      },
      [className, to.pathname, location.pathname],
    );

    return (
      <NavLink
        ref={ref}
        {...props}
        to={to}
        className={getClassName}
      />
    );
  },
);

ListItemComponent.propTypes = {
  className: PropTypes.string.isRequired,
  to: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

const NavButton = React.memo(
  ({ link, icon, name, className, badge, state, ...rest }) => {
    const to = useMemo(() => ({ pathname: link }), [link]);
    const combinedClassName = useMemo(
      () => `nav-list-item ${className || ''}`,
      [className],
    );

    return (
      <ListItem disablePadding>
        <ListItemButton
          disableGutters
          title={name}
          component={ListItemComponent}
          to={to}
          className={combinedClassName}
          state={state}
          {...rest}
        >
          {icon && (
            <Box
              display="flex"
              justifyContent="center"
              className="nav-icon-wrapper"
            >
              <Icon component={icon} className="nav-icon" />
            </Box>
          )}

          <ListItemText
            primary={name}
            sx={{
              flexGrow: 0,
              marginRight: 2,
            }}
          />

          {badge && badge}
        </ListItemButton>
      </ListItem>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.link === nextProps.link &&
      prevProps.icon === nextProps.icon &&
      prevProps.name === nextProps.name &&
      prevProps.className === nextProps.className &&
      prevProps.badge === nextProps.badge &&
      prevProps.id === nextProps.id &&
      prevProps['data-testid'] === nextProps['data-testid']
    );
  },
);

NavButton.displayName = 'NavButton';

NavButton.propTypes = {
  link: PropTypes.string.isRequired,
  icon: PropTypes.func,
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
  badge: PropTypes.node,
};

export default NavButton;
